"use client";

import { useCallback, useEffect, useEffectEvent, useState } from "react";

export type AsyncStatus = "idle" | "loading" | "ready" | "error";

export interface AsyncData<T> {
  status: AsyncStatus;
  /** Non-null only while `status === "ready"`. */
  data: T | null;
  /** Non-null only while `status === "error"`. */
  error: unknown;
  /** Re-run the loader for the current key. */
  reload: () => void;
}

interface Result<T> {
  key: string;
  attempt: number;
  data?: T;
  error?: unknown;
  failed: boolean;
}

/**
 * Load data for `key`, exposing loading / ready / error as one state machine.
 *
 * `key` identifies the request (slug + locale, …); pass `null` to stand down
 * (`status: "idle"`) until the inputs are ready. Loading is *derived* from the
 * key not matching the last result, so nothing has to be reset in an effect —
 * changing the key flips the status back to "loading" in the same render.
 *
 * `load` is wrapped in an effect event, so it always sees the latest props
 * without dragging every closure value into the dependency list.
 */
export function useAsyncData<T>(
  key: string | null,
  load: (signal: AbortSignal) => Promise<T>,
): AsyncData<T> {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<Result<T> | null>(null);
  const run = useEffectEvent((signal: AbortSignal) => load(signal));

  useEffect(() => {
    if (key == null) return;

    const controller = new AbortController();
    let cancelled = false;

    run(controller.signal).then(
      (data) => {
        if (!cancelled) setResult({ key, attempt, data, failed: false });
      },
      (error: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setResult({ key, attempt, error, failed: true });
      },
    );

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [key, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const current =
    result && result.key === key && result.attempt === attempt ? result : null;

  if (key == null) {
    return { status: "idle", data: null, error: null, reload };
  }
  if (!current) {
    return { status: "loading", data: null, error: null, reload };
  }
  if (current.failed) {
    return { status: "error", data: null, error: current.error, reload };
  }
  return { status: "ready", data: current.data ?? null, error: null, reload };
}
